import React from 'react';

import { ReactComponent as ArrowSVG } from '@pkg/assets/floorGame/arrow.svg';
import { type CardSizes, CurrencyIcon, ValuePrefix } from '@pkg/components';
import { type Collectible, OrderType, Side } from '@pkg/graphql/codegen/graphql';
import { getBaseCurrencyFromChain } from '@pkg/utils';

import { compare } from '@app/helpers';
import { getPricingData } from '@app/pages/ProfilePage/utils';
import { useBaseCurrencyStore } from '@app/store/zustand';

import { Icon, Label, PercentLabel, Price, PriceGroup, PriceRow, Value, Wrapper } from './styles';

interface CardPricesProps {
    cardSize?: CardSizes;
    floorPriceWei?: string | null;
    bordered?: boolean;
    item?: Collectible | null;
}

export const CardPrices = ({ cardSize, floorPriceWei, item, bordered = false }: CardPricesProps) => {
    const { id, orders } = item || { id: '' };
    const [chain] = id.split('.');
    const formatCurrency = useBaseCurrencyStore((state) => state.formatCurrency);
    const chainCurrency = getBaseCurrencyFromChain(chain);
    const latestOrderData = orders?.sort((a, b) => compare(a.expiresAt, b?.expiresAt))?.[0];
    const priceWei = latestOrderData?.price;

    const listingPriceWei =
        latestOrderData?.side === Side.Ask && latestOrderData?.type === OrderType.Limit ? priceWei : undefined;
    const pricing = floorPriceWei
        ? getPricingData({
              floorPriceWei: floorPriceWei,
              comparisonPrices: { listPrice: { value: listingPriceWei } },
          })
        : undefined;

    const fpd = pricing?.comparisonValues?.listPrice?.valueDiff;
    const value = pricing?.comparisonValues?.listPrice?.ethValue;
    const formattedValue = formatCurrency(chainCurrency, Number(value));

    const prefix = !chainCurrency ? null : (
        <ValuePrefix>
            <CurrencyIcon currency={chainCurrency} theme="dark" />
        </ValuePrefix>
    );
    return priceWei ? (
        <Wrapper $cardSize={cardSize} $bordered={bordered}>
            <PriceGroup>
                <Label $cardSize={cardSize}>List price</Label>
                <PriceRow>
                    <Price>
                        <Icon $cardSize={cardSize}>{prefix}</Icon>
                        <Value $cardSize={cardSize}>{formattedValue}</Value>
                        {fpd ? (
                            <PercentLabel $isDesc={fpd < 0}>
                                <ArrowSVG />
                                <span>{Math.abs(fpd).toFixed(1)}%</span>
                            </PercentLabel>
                        ) : null}
                    </Price>
                </PriceRow>
            </PriceGroup>
        </Wrapper>
    ) : null;
};
